import React from 'react';
import { Link } from 'react-router-dom';
import MaskedInput from 'react-text-mask';

import PropTypes from 'prop-types';
import Card from '@/components/common/Card';
import userPermissionImage from '@/components/common/icons/user-permission.svg';
import { USERNAME_MASK } from '@/helpers/mask';

const inputClasses =
  'h-[3em] px-2 border border-[#e3e3e3] rounded-[3px] !text-[11.5px] bg-white font-semibold text-[#919191] focus:outline-none focus:border-[#4d8fca] disabled:!bg-[#efefef] transition-colors !box-shadow-none';

const CreateUserComponent = ({
  busy,
  features,
  handleSelectedPermissionsChange,
  id,
  intl,
  onChange,
  onSubmit,
  status,
  user,
  cpfMask,
  cpfMaskPlaceholder,
  phoneMask,
  phoneMaskPlaceholder,
  cellphoneMask,
  cellphoneMaskLength,
}) => (
  <div className="flex flex-col p-[1.5em_1em] gap-[0.6em]">
    <Card className="fade-in !m-0">
      <h2 className="flex items-center !text-[1.25rem] !font-bold leading-normal text-[#5882c1] mb-4 border-b border-[#eee] pb-4">
        <div>
          <Link to={`/clients/${status}/${id}`} className="block">
            <div className="w-[14px] h-[14px] border-l-[3px] border-b-[3px] border-[#c1c1c1] rotate-45 transition-colors hover:border-[#5882c1] cursor-pointer" />
          </Link>
        </div>

        <div className="flex-shrink-0 w-14 flex items-center justify-center">
          <img src={userPermissionImage} alt={intl.formatMessage({ id: 'USER' })} className="h-7" />
        </div>

        {intl.formatMessage({ id: 'ADD_USER' })}
      </h2>

      <form onSubmit={onSubmit} className="flex flex-col gap-3">
        <div className="flex flex-col mx-12">
          <label htmlFor="name" className="block mb-1 text-[10px] uppercase text-[#393939] font-bold">
            {intl.formatMessage({ id: 'fullName' })}*
          </label>
          <input
            type="text"
            name="name"
            id="name"
            data-cy="name"
            required
            onChange={onChange}
            value={user.name || ''}
            placeholder="ex: Usuario Exemplo"
            className={inputClasses}
          />
        </div>

        <div className="flex flex-col mx-12">
          <label htmlFor="cpf" className="block mb-1 text-[10px] uppercase text-[#393939] font-bold">
            {intl.formatMessage({ id: 'CPF' })}
          </label>
          <MaskedInput
            type="text"
            name="cpf"
            id="cpf"
            data-cy="cpf"
            onChange={onChange}
            value={user.cpf || ''}
            placeholder={`ex: ${cpfMaskPlaceholder}`}
            guide={false}
            mask={cpfMask}
            className={inputClasses}
          />
        </div>
        <div className="flex flex-col mx-12">
          <label htmlFor="username" className="block mb-1 text-[10px] uppercase text-[#393939] font-bold">
            {intl.formatMessage({ id: 'USER' })}*
          </label>
          <MaskedInput
            type="text"
            name="username"
            id="username"
            data-cy="username"
            required
            onChange={onChange}
            value={user.username || ''}
            placeholder="ex: usuario_exemplo"
            guide={false}
            mask={USERNAME_MASK}
            className={inputClasses}
          />
        </div>

        <div className="flex flex-col mx-12">
          <label htmlFor="email" className="block mb-1 text-[10px] uppercase text-[#393939] font-bold">
            {intl.formatMessage({ id: 'EMAIL' })}*
          </label>
          <input
            type="email"
            name="email"
            id="email"
            data-cy="email"
            required
            onChange={onChange}
            value={user.email || ''}
            placeholder="ex: usuario_exemplo@redevistorias.com.br"
            className={inputClasses}
          />
        </div>
        <div className="flex flex-col mx-12">
          <label htmlFor="phone" className="block mb-1 text-[10px] uppercase text-[#393939] font-bold">
            {intl.formatMessage({ id: 'CONTACT' })}
          </label>
          <MaskedInput
            type="text"
            name="phone"
            id="phone"
            data-cy="phone"
            onChange={onChange}
            value={user.phone || ''}
            placeholder={`ex: ${phoneMaskPlaceholder}`}
            guide={false}
            mask={value => (value.length >= cellphoneMaskLength ? cellphoneMask : phoneMask)}
            className={inputClasses}
          />
        </div>

        <div className="flex justify-between items-center flex-wrap my-1 px-12 text-center">
          {features.length ? (
            features.map(feature => (
              <div key={feature.id} className="flex items-center w-[calc(50%-5px)] h-6 overflow-hidden">
                <label
                  title={intl.formatMessage({ id: `PERMISSION.${feature.description}` })}
                  className="flex items-center m-0 whitespace-nowrap overflow-hidden text-ellipsis text-[10px] font-bold uppercase text-[#393939] line-height-none cursor-pointer"
                >
                  <input
                    type="checkbox"
                    name="feature"
                    data-cy={`feature-${feature.description}`}
                    value={feature.id}
                    onChange={handleSelectedPermissionsChange}
                    defaultChecked={feature?.rule === 'opt-out'}
                    className="!mr-[0.33rem] align-middle"
                  />
                  <span>{intl.formatMessage({ id: `PERMISSION.${feature.description}` })}</span>
                </label>
              </div>
            ))
          ) : (
            <div className="w-full text-center text-[#919191] text-[10px] font-bold uppercase py-4">
              {intl.formatMessage({ id: 'NOT_FOUND_MESSAGE' })}
            </div>
          )}
        </div>

        <div className="flex justify-center pt-8 mt-4 border-t border-[#eeeeee] gap-4">
          <Link
            to={`/clients/${status}/${id}`}
            className="flex items-center px-[19px] h-9 border-0 !rounded-[3px] font-bold text-center !text-[#FFFFFF] hover:opacity-75 transition-colors text-[0.875rem] bg-[#c1c1c1] !no-underline min-w-[140px] justify-center"
          >
            {intl.formatMessage({ id: 'CANCEL' })}
          </Link>

          <button
            type="submit"
            className="flex items-center px-[19px] h-9 border-0 !rounded-[3px] font-bold text-center !text-[#FFFFFF] hover:opacity-75 transition-colors text-[0.875rem] bg-[#97a500] !no-underline min-w-[140px] justify-center"
            disabled={busy}
          >
            {intl.formatMessage({ id: 'SAVE' })}
          </button>
        </div>
      </form>
    </Card>
  </div>
);

CreateUserComponent.propTypes = {
  busy: PropTypes.bool.isRequired,
  features: PropTypes.array.isRequired,
  handleSelectedPermissionsChange: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
  intl: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  status: PropTypes.string.isRequired,
  user: PropTypes.object.isRequired,
  cpfMask: PropTypes.array.isRequired,
  cpfMaskPlaceholder: PropTypes.string.isRequired,
  phoneMask: PropTypes.array.isRequired,
  phoneMaskPlaceholder: PropTypes.string.isRequired,
  cellphoneMask: PropTypes.array.isRequired,
  cellphoneMaskLength: PropTypes.number.isRequired,
};

export default CreateUserComponent;
